import { logger } from '@/lib/logger';
import React, { useEffect, useMemo, ReactNode, useState } from 'react';
import { Appearance } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { StripePaymentForm } from './StripePaymentForm';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { useTheme } from '@/lib/theme-provider';
import { getStripeInstance } from '@/lib/stripe/preloader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';

// Guest email form component for collecting email before payment
interface GuestEmailFormProps {
  guestEmail: string;
  onGuestEmailChange?: (email: string) => void;
  isLoading: boolean;
}

function GuestEmailForm({ guestEmail, onGuestEmailChange, isLoading }: GuestEmailFormProps) {
  const [localEmail, setLocalEmail] = useState(guestEmail);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidEmail(localEmail)) {
      setHasSubmitted(true);
      onGuestEmailChange?.(localEmail);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="guest-email" className="text-sm font-medium">
          Email address
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            id="guest-email"
            type="email"
            placeholder="you@example.com"
            value={localEmail}
            onChange={(e) => setLocalEmail(e.target.value)}
            className="pl-10"
            disabled={isLoading || hasSubmitted}
            required
            autoFocus
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          We'll create your account and send login instructions to this email.
        </p>
      </div>

      <Button
        type="submit"
        disabled={!isValidEmail(localEmail) || isLoading || hasSubmitted}
        className="w-full bg-jdorange hover:bg-jdorange/90"
      >
        {isLoading || hasSubmitted ? (
          <>
            <LoadingSpinner className="h-4 w-4 mr-2" />
            Loading payment form...
          </>
        ) : (
          'Continue to Payment'
        )}
      </Button>
    </form>
  );
}

interface StripeCheckoutDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to call when the dialog is closed
   */
  onClose: () => void;

  /**
   * The client secret from the PaymentIntent or SetupIntent
   */
  clientSecret: string;

  /**
   * Optional title for the dialog
   * @default "Complete your payment"
   */
  title?: string;

  /**
   * Optional description for the dialog
   */
  description?: string | ReactNode;

  /**
   * Optional callback for successful payment
   */
  onSuccess?: (paymentIntentId: string) => void;

  /**
   * Optional callback for payment errors
   */
  onError?: (error: any) => void;

  /**
   * Optional tier name for tracking subscription type
   */
  tier?: string;

  /**
   * Whether this is a guest checkout (no authenticated user)
   */
  isGuestCheckout?: boolean;

  /**
   * Guest email for guest checkout flow
   */
  guestEmail?: string;

  /**
   * Callback when guest email changes
   */
  onGuestEmailChange?: (email: string) => void;

  /**
   * Whether we're currently fetching the client secret (for guest checkout loading state)
   */
  isFetchingClientSecret?: boolean;
}

/**
 * A shared component for displaying a Stripe checkout dialog
 * with Payment Elements UI for collecting payment information.
 */
export function StripeCheckoutDialog({
  open,
  onClose,
  clientSecret,
  title = "Complete your payment",
  description,
  onSuccess,
  onError,
  tier,
  isGuestCheckout = false,
  guestEmail = '',
  onGuestEmailChange,
  isFetchingClientSecret = false
}: StripeCheckoutDialogProps) {
  const { theme } = useTheme();
  
  // Use the cached Stripe instance from the preloader
  const stripePromise = useMemo(() => {
    logger.debug('Getting Stripe instance from preloader');
    return getStripeInstance();
  }, []);
  
  // Log key information when props change
  useEffect(() => {
    logger.debug('StripeCheckoutDialog:', {
      open,
      hasClientSecret: !!clientSecret,
      stripeLoaded: !!stripePromise,
      currentTheme: theme
    });
    
    if (!stripePromise) {
      logger.warn('Stripe has not loaded yet. Check VITE_STRIPE_PUBLISHABLE_KEY environment variable.');
    }
    
    if (!clientSecret) {
      logger.warn('No client secret provided to StripeCheckoutDialog.');
    }
  }, [open, clientSecret, theme, stripePromise]);
  
  // Options for the Elements provider
  const stripeElementsOptions = clientSecret ? {
    clientSecret,
    appearance: {
      theme: theme === 'dark' ? 'night' : 'stripe',
      variables: {
        colorPrimary: '#F37022', // Orange from theme
        colorBackground: theme === 'dark' ? '#1f2937' : '#ffffff',
        colorText: theme === 'dark' ? '#e5e7eb' : '#1f2937',
        colorDanger: theme === 'dark' ? '#ef4444' : '#dc2626',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
      rules: {
        '.Label': {
          color: theme === 'dark' ? '#e5e7eb' : '#1f2937',
        },
        '.Input': {
          color: theme === 'dark' ? '#e5e7eb' : '#1f2937',
          backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
          borderColor: theme === 'dark' ? '#4b5563' : '#e5e7eb',
        },
        '.Input:hover': {
          borderColor: theme === 'dark' ? '#6b7280' : '#d1d5db',
        },
        '.Input:focus': {
          borderColor: '#F37022',
        },
        '.Error': {
          color: theme === 'dark' ? '#f87171' : '#dc2626',
        },
        '.Tab': {
          backgroundColor: theme === 'dark' ? '#374151' : '#f3f4f6',
          color: theme === 'dark' ? '#e5e7eb' : '#1f2937',
        },
        '.Tab:hover': {
          backgroundColor: theme === 'dark' ? '#4b5563' : '#e5e7eb',
        },
        '.Tab--selected': {
          backgroundColor: theme === 'dark' ? '#4b5563' : '#ffffff',
          color: theme === 'dark' ? '#ffffff' : '#000000',
          borderColor: '#F37022',
        }
      }
    } as Appearance
  } : undefined;
  
  const handleError = (error: any) => {
    logger.error('Payment error:', error);
    toast.error(error.message || 'Payment failed');
    if (onError) onError(error);
  };
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) onClose();
    }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        
        {!stripePromise ? (
          <div className="flex flex-col items-center py-6 space-y-4">
            <LoadingSpinner className="h-8 w-8" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading payment form...</p>
            <p className="text-xs text-red-500 dark:text-red-400">If this persists, check that Stripe API keys are configured correctly.</p>
          </div>
        ) : isGuestCheckout && (!clientSecret || clientSecret === 'loading') ? (
          // Guest checkout: show email input first, then fetch client secret
          <GuestEmailForm
            guestEmail={guestEmail}
            onGuestEmailChange={onGuestEmailChange}
            isLoading={isFetchingClientSecret}
          />
        ) : !clientSecret || clientSecret === 'loading' ? (
          <div className="flex flex-col items-center py-6 space-y-4">
            <LoadingSpinner className="h-8 w-8" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Preparing your payment form...</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">This should only take a moment.</p>
          </div>
        ) : (
          <Elements options={stripeElementsOptions} stripe={stripePromise}>
            <StripePaymentForm
              clientSecret={clientSecret}
              onSuccess={onSuccess}
              onError={handleError}
              tier={tier}
              isGuestCheckout={isGuestCheckout}
              guestEmail={guestEmail}
              onGuestEmailChange={onGuestEmailChange}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
} 